import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatSessionItem } from '../api/chat'
import { Icon } from '../../public/icons/Icon.tsx'
import { Pencil } from '../icons/Pencil.tsx'
import { Basket } from '../icons/Basket.tsx'
import { getModelVisuals } from '../utils/modelVisuals'
import { DARK_ICONS } from './ModelIcon'

const SESSION_MODEL_PREFIX = 'session_model_'

function getSessionModelFromStorage(sessionId: string): string | null {
	try {
		const saved = localStorage.getItem(`${SESSION_MODEL_PREFIX}${sessionId}`)
		if (saved) {
			const model = JSON.parse(saved) as { backendModel?: string }
			return model?.backendModel || null
		}
	} catch {
		/* нет сохранённой модели */
	}
	return null
}

type Props = {
	collapsed: boolean
	userExists: boolean
	sessions: ChatSessionItem[]
	currentSessionId: string | null
	onOpenSession: (
		sessionId: string,
		agentInfo?: {
			agentId?: string | null
			agentTitle?: string | null
			agentIconUrl?: string | null
		},
		kind?: 'chat' | 'assistant',
		row?: ChatSessionItem,
	) => void
	onDeleteSession: (e: React.MouseEvent, sessionId: string) => void
	onRenameSession: (sessionId: string, title: string) => Promise<void>
	/** Закрепить / открепить строку (23.10): чат или помощник — закреплённые стоят первой группой. Без обработчика
	 *  кнопки скрепки нет (старые вызовы и тесты списка). */
	onPinSession?: (e: React.MouseEvent, sessionId: string) => void
	/** Открытый раздел помощника (/assistants/<слуг>) — его строка в меню подсвечена. */
	currentAgentSlug?: string | null
	/** Есть ли у сервера ещё диалоги за показанными (useSidebarSessions): тогда под списком стоит метка подгрузки. */
	hasMore?: boolean
	/** Метка под списком попала в экран — просим следующую страницу. */
	onLoadMore?: () => void
}

type PopoverMode = 'menu' | 'confirm'

const timeAgo = (dateStr: string): string => {
	const now = new Date()
	const date = new Date(dateStr)
	const diffMs = now.getTime() - date.getTime()
	const diffDays = Math.floor(diffMs / 86400000)

	if (diffDays === 0) return 'chats.today'
	if (diffDays === 1) return 'chats.yesterday'
	if (diffDays < 7) return 'chats.thisWeek'
	if (diffDays < 30) return 'chats.thisMonth'
	return 'chats.earlier'
}

const groupSessions = (sessions: ChatSessionItem[]) => {
	const groups: Record<string, ChatSessionItem[]> = {}

	for (const session of sessions) {
		// Закреплённые — своей группой наверху (владелец 23.10), остальные — по времени.
		const label = session.is_pinned ? 'chats.pinned' : timeAgo(session.updated_at)
		if (!groups[label]) groups[label] = []
		groups[label].push(session)
	}

	return groups
}

const groupOrder = ['chats.pinned', 'chats.today', 'chats.yesterday', 'chats.thisWeek', 'chats.thisMonth', 'chats.earlier'] as const

const groupCollapsedLabel: Record<string, string> = {
	'chats.pinned': 'chats.pinnedShort',
	'chats.today': 'chats.todayShort',
	'chats.yesterday': 'chats.yesterdayShort',
	'chats.thisWeek': 'chats.thisWeekShort',
	'chats.thisMonth': 'chats.thisMonthShort',
	'chats.earlier': 'chats.earlierShort',
}

const SidebarChats = ({
	collapsed,
	userExists,
	sessions,
	currentSessionId,
	onOpenSession,
	onDeleteSession,
	onRenameSession,
	onPinSession,
	currentAgentSlug = null,
	hasMore = false,
	onLoadMore,
}: Props) => {
	const { t } = useTranslation()
	const [hoveredSession, setHoveredSession] = useState<string | null>(null)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [editTitle, setEditTitle] = useState('')
	const [popoverSessionId, setPopoverSessionId] = useState<string | null>(null)
	const [popoverMode, setPopoverMode] = useState<PopoverMode>('menu')
	const [isHoverDevice, setIsHoverDevice] = useState(false)

	const grouped = useMemo(() => groupSessions(sessions), [sessions])
	const rootRef = useRef<HTMLDivElement | null>(null)

	// Подгрузка по скроллу (владелец 17.10: «по скролу и всегда отдавать по 30»): метка в конце списка пересеклась с
	// его окном (список прокручивается сам, rootMargin 200px — заранее) → следующая страница. Перевзводится после
	// каждой пришедшей страницы: если список короче окна, метка видна и дальше, и страницы идут, пока окно не заполнится.
	const listRef = useRef<HTMLDivElement | null>(null)
	const sentinelRef = useRef<HTMLDivElement | null>(null)
	useEffect(() => {
		const el = sentinelRef.current
		// Без наблюдателя (очень старый WebView) панель просто остаётся на первых 30, а не падает вместе с меню сайта.
		if (!el || !hasMore || !onLoadMore || typeof IntersectionObserver === 'undefined') return
		const io = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) onLoadMore()
			},
			{ root: listRef.current, rootMargin: '200px' },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [hasMore, onLoadMore, sessions.length])

	useEffect(() => {
		if (typeof window === 'undefined') return

		const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)')

		const updateHoverDevice = () => {
			setIsHoverDevice(mediaQuery.matches)
		}

		updateHoverDevice()

		if (typeof mediaQuery.addEventListener === 'function') {
			mediaQuery.addEventListener('change', updateHoverDevice)
			return () => mediaQuery.removeEventListener('change', updateHoverDevice)
		}

		mediaQuery.addListener(updateHoverDevice)
		return () => mediaQuery.removeListener(updateHoverDevice)
	}, [])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent | TouchEvent) => {
			if (!rootRef.current) return

			if (!rootRef.current.contains(event.target as Node)) {
				setPopoverSessionId(null)
				setPopoverMode('menu')
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('touchstart', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('touchstart', handleClickOutside)
		}
	}, [])

	useEffect(() => {
		setPopoverSessionId(null)
		setPopoverMode('menu')
	}, [currentSessionId])

	const startRename = (e: React.MouseEvent, session: ChatSessionItem) => {
		e.stopPropagation()
		setHoveredSession(null)
		setPopoverSessionId(null)
		setPopoverMode('menu')
		setEditingId(session.id)
		setEditTitle(session.title)
	}

	const finishRename = async (sessionId: string) => {
		const trimmed = editTitle.trim()

		try {
			if (trimmed && trimmed !== sessions.find((session) => session.id === sessionId)?.title) {
				await onRenameSession(sessionId, trimmed)
			}
		} finally {
			setEditingId(null)
			setEditTitle('')
		}
	}

	const toggleMobilePopover = (e: React.MouseEvent, sessionId: string) => {
		e.stopPropagation()
		setHoveredSession(null)
		setEditingId(null)
		setEditTitle('')

		if (popoverSessionId === sessionId) {
			setPopoverSessionId(null)
			setPopoverMode('menu')
			return
		}

		setPopoverSessionId(sessionId)
		setPopoverMode('menu')
	}

	const openConfirmPopover = (e: React.MouseEvent, sessionId: string) => {
		e.stopPropagation()
		setHoveredSession(null)
		setEditingId(null)
		setEditTitle('')
		setPopoverSessionId(sessionId)
		setPopoverMode('confirm')
	}

	const backToMenu = (e: React.MouseEvent, sessionId: string) => {
		e.stopPropagation()

		if (isHoverDevice) {
			setPopoverSessionId(null)
			setPopoverMode('menu')
			return
		}

		setPopoverSessionId(sessionId)
		setPopoverMode('menu')
	}

	const confirmDelete = (e: React.MouseEvent, sessionId: string) => {
		e.stopPropagation()
		setHoveredSession(null)
		setPopoverSessionId(null)
		setPopoverMode('menu')
		onDeleteSession(e, sessionId)
	}

	if (!userExists || sessions.length === 0) return null

	return (
		<div className="sidebar-chats" ref={rootRef}>
			<div className="sidebar-chats-title">{t('chats.title')}</div>

			<div className="sidebar-chats-list" ref={listRef}>
				{groupOrder.map((group) => {
					const items = grouped[group]
					if (!items || items.length === 0) return null

					return (
						<div key={group} className="sidebar-chat-group">
							<div className="sidebar-chat-group-label">
								{collapsed ? t(groupCollapsedLabel[group]) : t(group)}
							</div>

							{items.map((session) => {
								const isActive = session.kind === 'assistant'
									? !!currentAgentSlug && session.agent_slug === currentAgentSlug
									: currentSessionId === session.id
								// Строка помощника: имя не переименовать и не удалить — только закрепить; клик открывает последнюю работу.
								const isAssistant = session.kind === 'assistant'
								const canRename = !isAssistant && !session.agent_title
								const isEditing = editingId === session.id
								const isPopoverOpen = popoverSessionId === session.id

								const showDesktopActions =
									isHoverDevice && hoveredSession === session.id && !isEditing && !isPopoverOpen

								const showMobileMenuButton = !isHoverDevice && isActive && !isEditing
								const showPopoverAnchor = !isEditing && (showMobileMenuButton || isPopoverOpen)

								return (
									<div
										key={session.id}
										className={`sidebar-chat-item ${isActive ? 'active' : ''} ${isPopoverOpen ? 'is-popover-open' : ''} ${showDesktopActions ? 'is-actions-visible' : ''}`}
										onClick={() => {
											if (!isEditing && !isPopoverOpen) {
												// Прокидываем agent_* поля сессии в Chat через router state,
												// чтобы при клике на сессию-помощника заголовок/иконка
												// отображались сразу, до завершения loadSession.
												onOpenSession(session.id, {
													agentId: session.agent_id,
													agentTitle: session.agent_title,
													agentIconUrl: session.agent_icon_url,
												}, session.kind, session)
											}
										}}
										onMouseEnter={() => {
											if (isHoverDevice) setHoveredSession(session.id)
										}}
										onMouseLeave={() => {
											if (isHoverDevice) setHoveredSession(null)
										}}
										title={isEditing ? '' : session.title}
									>
										{isEditing ? (
											<div className="sidebar-chat-rename" onClick={(e) => e.stopPropagation()}>
												<input
													className="sidebar-chat-rename-input"
													value={editTitle}
													onChange={(e) => setEditTitle(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															void finishRename(session.id)
														}

														if (e.key === 'Escape') {
															setEditingId(null)
															setEditTitle('')
														}
													}}
													autoFocus
												/>

												<button
													type="button"
													className="sidebar-chat-rename-save"
													onMouseDown={(e) => e.preventDefault()}
													onClick={() => void finishRename(session.id)}
													title={t('common.save')}
													aria-label={t('common.save')}
												>
													OK
												</button>
											</div>
										) : (
											<span className="sidebar-chat-title">
												{session.agent_icon_url ? (
													<img src={session.agent_icon_url} alt="" className="sidebar-chat-title__agent-icon" />
												) : !session.agent_title ? (() => {
													const modelStr = getSessionModelFromStorage(session.id) || session.model
													if (!modelStr) return null
													const { icon, name } = getModelVisuals(modelStr)
													return icon ? (
														<img
															src={icon}
															alt={name}
															className={`sidebar-chat-title__model-icon${DARK_ICONS.includes(icon) ? ' model-icon-dark' : ''}`}
														/>
													) : null
												})() : null}
												<span className="sidebar-chat-title__text">{isAssistant ? session.title : session.agent_title || session.title}</span>
											</span>
										)}

										<div
											className={`sidebar-chat-actions ${showDesktopActions ? 'is-visible' : ''}`}
										>
											{canRename && (
											<button
												type="button"
												className="sidebar-chat-action-btn action-btn"
												onClick={(e) => startRename(e, session)}
												title={t('chats.rename')}
											>
												<Pencil size={12} className="sidebar-chat-btn-pencil" />
											</button>
											)}

											{onPinSession && (
											<button
												type="button"
												className="sidebar-chat-action-btn action-btn"
												onClick={(e) => onPinSession(e, session.id)}
												title={session.is_pinned ? t('chats.unpin') : t('chats.pin')}
											>
												{/* Та же скрепка, что в рельсе помощников: по диагонали, закрашена у закреплённого; цвет — классом, как у карандаша. */}
												<span className={`sidebar-chat-btn-pin${session.is_pinned ? ' is-on' : ''}`} aria-hidden="true"><Icon id={session.is_pinned ? 'gen-pin' : 'gen-pin-outline'} size={13} /></span>
											</button>
											)}

											{!isAssistant && (
											<button
												type="button"
												className="sidebar-chat-action-btn sidebar-chat-delete"
												onClick={(e) => openConfirmPopover(e, session.id)}
												title={t('common.delete')}
											>
												<Basket size={12} className="sidebar-chat-btn-basket" />
											</button>
											)}
										</div>

										{showPopoverAnchor && (
											<div
												className={`sidebar-chat-popover-anchor ${isPopoverOpen ? 'is-open' : ''}`}
												onClick={(e) => e.stopPropagation()}
											>
												{showMobileMenuButton && (
													<button
														type="button"
														className={`sidebar-chat-mobile-menu-btn ${isPopoverOpen ? 'is-open' : ''}`}
														title={isPopoverOpen ? t('chats.closeActions') : t('chats.actions')}
														aria-label={isPopoverOpen ? t('chats.closeActions') : t('chats.actions')}
														onClick={(e) => toggleMobilePopover(e, session.id)}
													>
														{isPopoverOpen ? (
															<span className="sidebar-chat-mobile-menu-close" aria-hidden="true">
																<span />
																<span />
															</span>
														) : (
															<span className="sidebar-chat-mobile-menu-dots" aria-hidden="true">
																<span />
																<span />
																<span />
															</span>
														)}
													</button>
												)}

												<div
													className={`sidebar-chat-popover-shell ${isPopoverOpen ? 'is-visible' : ''}`}
												>
													<div
														className={`sidebar-chat-popover ${popoverMode === 'confirm' ? 'is-confirm-mode' : 'is-menu-mode'}`}
														onClick={(e) => e.stopPropagation()}
													>
														<div className="sidebar-chat-popover-views">
															<div className="sidebar-chat-popover-view sidebar-chat-popover-view--menu">
																{canRename && (
																<button
																	type="button"
																	className="sidebar-chat-popover-btn"
																	onClick={(e) => startRename(e, session)}
																>
																	<Pencil size={12} className="sidebar-chat-btn-pencil" />
																	<span>{t('chats.rename')}</span>
																</button>
																)}

																{onPinSession && (
																<button
																	type="button"
																	className="sidebar-chat-popover-btn"
																	onClick={(e) => { setPopoverSessionId(null); onPinSession(e, session.id) }}
																>
																	<span className={`sidebar-chat-btn-pin${session.is_pinned ? ' is-on' : ''}`} aria-hidden="true"><Icon id={session.is_pinned ? 'gen-pin' : 'gen-pin-outline'} size={13} /></span>
																	<span>{session.is_pinned ? t('chats.unpin') : t('chats.pin')}</span>
																</button>
																)}

																{!isAssistant && (
																<button
																	type="button"
																	className="sidebar-chat-popover-btn is-danger"
																	onClick={(e) => openConfirmPopover(e, session.id)}
																>
																	<Basket size={12} className="sidebar-chat-btn-basket" />
																	<span>{t('common.delete')}</span>
																</button>
																)}
															</div>

															<div className="sidebar-chat-popover-view sidebar-chat-popover-view--confirm">
																<div className="sidebar-chat-popover-title">{t('chats.confirmDelete')}</div>

																<div className="sidebar-chat-popover-footer">
																	<button
																		type="button"
																		className="sidebar-chat-popover-btn"
																		onClick={(e) => backToMenu(e, session.id)}
																	>
																		<span>{t('common.no')}</span>
																	</button>

																	<button
																		type="button"
																		className="sidebar-chat-popover-btn is-danger"
																		onClick={(e) => confirmDelete(e, session.id)}
																	>
																		<span>{t('common.yes')}</span>
																	</button>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										)}
									</div>
								)
							})}
						</div>
					)
				})}
				{hasMore && <div ref={sentinelRef} className="sidebar-chats-sentinel" aria-hidden="true" />}
			</div>
		</div>
	)
}

export default SidebarChats
